import { Flame, RefreshCw, HelpCircle } from "lucide-react";
import { useSeoAnalysisCard } from "../../hooks/use-seo-analysis-card";
import { BrandDetails } from "../../types/add-brand-types";
import { SeoOpportunityItem } from "../../types/strategy";
import Shimmer from "../shimmer/Shimmer";
import Toast from "../uiComponents/Toast";

interface SEOAnalysisCardProps {
  brandData: BrandDetails;
  onSeoItemClick: (
    keyword: string,
    weeklyPlanItemId?: string,
    angle?: string
  ) => void;
}

const ANGLE_LABELS: Record<string, string> = {
  educational: "Educational",
  problem_aware: "Problem-aware",
  comparison: "Comparison",
  product_led: "Product-led",
};

const ANGLE_STYLES: Record<string, { backgroundColor: string; color: string }> =
  {
    educational: { backgroundColor: "#e0f2fe", color: "#075985" },
    problem_aware: { backgroundColor: "#ffedd5", color: "#9a3412" },
    comparison: { backgroundColor: "#ede9fe", color: "#6d28d9" },
    product_led: { backgroundColor: "#dcfce7", color: "#166534" },
    fallback: { backgroundColor: "#f3f4f6", color: "#4b5563" },
  };

const getOpportunityAngle = (opportunity: SeoOpportunityItem) => {
  const angle = opportunity.angle?.toLowerCase();
  if (angle && angle in ANGLE_LABELS) {
    return angle;
  }
  return "fallback";
};

const getIntentStyle = (intent: string) => {
  const normalizedIntent = intent?.toLowerCase();
  if (normalizedIntent === "informational") {
    return { backgroundColor: "#dbeafe", color: "#1e40af" };
  }
  if (normalizedIntent === "transactional") {
    return { backgroundColor: "#fee2e2", color: "#991b1b" };
  }
  return { backgroundColor: "#d1fae5", color: "#065f46" };
};

const SEOAnalysisCard: React.FC<SEOAnalysisCardProps> = ({
  brandData,
  onSeoItemClick,
}) => {
  const {
    seoOpportunities,
    loadingSeo,
    generateSeoOpportunities,
    lastAuditTime,
    canRefresh,
    getDaysUntilNextRefresh,
    toast,
    showSeoTooltip,
    setShowSeoTooltip,
    showRefreshTooltip,
    setShowRefreshTooltip,
    loadingMessageIndex,
    formatDate,
    loadingMessages,
  } = useSeoAnalysisCard(brandData);

  return (
    <>
      {toast && <Toast {...toast} />}
      <div className="analysis-card">
        <div className="card-header" style={{ marginBottom: "16px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Flame size={22} style={{ color: "#f59e0b" }} />
              <div className="card-title">SEO Opportunities</div>
              {seoOpportunities.length > 0 && (
                <>
                  <div
                    style={{
                      position: "relative",
                      display: "inline-block",
                    }}
                    onMouseEnter={() => setShowRefreshTooltip(true)}
                    onMouseLeave={() => setShowRefreshTooltip(false)}
                  >
                    <button
                      onClick={() => generateSeoOpportunities(true)}
                      disabled={loadingSeo || !canRefresh()}
                      style={{
                        background: "none",
                        border: "none",
                        cursor:
                          loadingSeo || !canRefresh()
                            ? "not-allowed"
                            : "pointer",
                        padding: "4px",
                        display: "flex",
                        alignItems: "center",
                        color: !canRefresh() ? "#d1d5db" : "#6b7280",
                        transition: "color 0.2s ease",
                        opacity: !canRefresh() ? 0.5 : 1,
                      }}
                      onMouseEnter={(e) =>
                        !loadingSeo &&
                        canRefresh() &&
                        (e.currentTarget.style.color = "#4f46e5")
                      }
                      onMouseLeave={(e) =>
                        canRefresh() &&
                        (e.currentTarget.style.color = "#6b7280")
                      }
                    >
                      <RefreshCw
                        size={16}
                        style={{
                          transform: loadingSeo
                            ? "rotate(360deg)"
                            : "rotate(0deg)",
                          transition: loadingSeo
                            ? "transform 1s linear infinite"
                            : "transform 0.2s ease",
                        }}
                      />
                    </button>
                    {showRefreshTooltip && (
                      <div
                        style={{
                          position: "absolute",
                          top: "28px",
                          left: "50%",
                          transform: "translateX(-50%)",
                          backgroundColor: "#1f2937",
                          color: "white",
                          padding: "8px 12px",
                          borderRadius: "6px",
                          fontSize: "12px",
                          whiteSpace: "nowrap",
                          zIndex: 1000,
                          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                          pointerEvents: "none",
                        }}
                      >
                        {!canRefresh()
                          ? `Next refresh available in ${getDaysUntilNextRefresh()} ${
                              getDaysUntilNextRefresh() === 1 ? "day" : "days"
                            }. Please check back then.`
                          : "Refresh SEO Opportunities"}
                      </div>
                    )}
                  </div>
                  <div
                    style={{
                      position: "relative",
                      display: "inline-block",
                    }}
                    onMouseEnter={() => setShowSeoTooltip(true)}
                    onMouseLeave={() => setShowSeoTooltip(false)}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#6b7280",
                        cursor: "help",
                      }}
                      aria-label="SEO Opportunities help"
                    >
                      <HelpCircle size={16} />
                    </div>
                    {showSeoTooltip && (
                      <div
                        style={{
                          position: "absolute",
                          top: "24px",
                          left: "50%",
                          transform: "translateX(-50%)",
                          backgroundColor: "#1f2937",
                          color: "white",
                          padding: "12px 16px",
                          borderRadius: "8px",
                          fontSize: "13px",
                          width: "320px",
                          zIndex: 1000,
                          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                          lineHeight: "1.5",
                        }}
                      >
                        <div style={{ fontWeight: "600", marginBottom: "6px" }}>
                          SEO Opportunities
                        </div>
                        <div style={{ fontSize: "12px", color: "#d1d5db" }}>
                          Search Engine Optimization helps improve your brand's
                          visibility in search results. These opportunities are
                          high-potential keywords and topics tailored to your
                          brand. Refreshes aim to explore new search angles, but
                          may still surface related themes when they are
                          strategically relevant.
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            {lastAuditTime && seoOpportunities.length > 0 && (
              <span
                style={{
                  fontSize: "12px",
                  color: "#6b7280",
                  fontStyle: "italic",
                }}
              >
                Last audit: {formatDate(lastAuditTime, "MMM DD, YYYY HH:mm")}
              </span>
            )}
          </div>
        </div>

        {loadingSeo ? (
          // Shimmer Loading State
          <div>
            <div
              key={loadingMessageIndex}
              className="seo-loading-message"
              style={{
                textAlign: "center",
                marginBottom: "16px",
                color: "#4f46e5",
                fontSize: "14px",
                fontWeight: "500",
                minHeight: "21px",
              }}
            >
              {loadingMessages[loadingMessageIndex]}
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
              }}
            >
              {[1, 2, 3].map((item) => (
                <div
                  key={item}
                  style={{
                    padding: "16px",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginBottom: "8px",
                    }}
                  >
                    <Shimmer width={150} height={20} />
                    <Shimmer width={100} height={20} borderRadius={4} />
                  </div>
                  <Shimmer width="100%" height={16} />
                  <Shimmer
                    width="80%"
                    height={16}
                    style={{ marginTop: "4px" }}
                  />
                </div>
              ))}
            </div>
          </div>
        ) : seoOpportunities.length === 0 ? (
          // Empty State
          <div
            style={{
              textAlign: "center",
              padding: "24px 16px",
            }}
          >
            <div
              style={{
                color: "#6b7280",
                fontSize: "14px",
                marginBottom: "16px",
                lineHeight: "1.5",
              }}
            >
              Discover search opportunities relevant to your brand.
              <br />
              We analyze your brand data to find high-potential keywords and
              topics.
            </div>
            <button
              onClick={() => generateSeoOpportunities(true)}
              disabled={loadingSeo}
              style={{
                padding: "10px 20px",
                backgroundColor: "#4f46e5",
                color: "white",
                border: "none",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.2s ease",
                boxShadow: "0 2px 4px rgba(79, 70, 229, 0.2)",
              }}
            >
              Generate Opportunities
            </button>
          </div>
        ) : (
          // List Items State
          <div
            className="seo-opportunities-scroll"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            {seoOpportunities.map((opportunity, index) => {
              const angle = getOpportunityAngle(opportunity);
              const angleStyle = ANGLE_STYLES[angle] ?? ANGLE_STYLES.fallback;
              const intentStyle = getIntentStyle(opportunity.intent);

              return (
                <div
                  key={index}
                  style={{
                    padding: "16px",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    transition: "all 0.2s ease",
                    cursor: "pointer",
                  }}
                  onClick={() =>
                    onSeoItemClick(opportunity.keyword, undefined, angle)
                  }
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow =
                      "0 4px 12px rgba(79, 70, 229, 0.15)";
                    e.currentTarget.style.borderColor = "#4f46e5";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = "none";
                    e.currentTarget.style.borderColor = "#e5e7eb";
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginBottom: "8px",
                      flexWrap: "wrap",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "15px",
                        fontWeight: "600",
                        color: "#1f2937",
                      }}
                    >
                      {opportunity.keyword}
                    </div>
                    <span
                      style={{
                        fontSize: "11px",
                        fontWeight: "600",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        backgroundColor: angleStyle.backgroundColor,
                        color: angleStyle.color,
                      }}
                    >
                      {ANGLE_LABELS[angle] ?? "SEO idea"}
                    </span>
                    <span
                      style={{
                        fontSize: "11px",
                        fontWeight: "600",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        backgroundColor: intentStyle.backgroundColor,
                        color: intentStyle.color,
                      }}
                    >
                      {opportunity.intent}
                    </span>
                    {opportunity.is_generated && (
                      <span
                        title="An SEO article has already been generated for this item."
                        style={{
                          fontSize: "11px",
                          fontWeight: "700",
                          padding: "4px 8px",
                          borderRadius: "999px",
                          backgroundColor: "#ecfdf5",
                          color: "#047857",
                          border: "1px solid #a7f3d0",
                        }}
                      >
                        Generated
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#6b7280",
                      lineHeight: "1.5",
                    }}
                  >
                    {opportunity.reason}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

export default SEOAnalysisCard;
